import angular from 'angular';
import moment from 'moment';
import { PortariaParametrizacaoModalService } from './modals/parametrizacao/portaria-parametrizacao.modal.service';
import {
    PortariaEntity,
    PortariaHistoricoEntity,
    PortariaHistoricoItemView,
    PortariaResponsavelForm,
} from './models/portaria.model';

export class ConfiguracoesPortariaController {
    static $inject = [
        '$http',
        'nsjRouting',
        'NewToaster',
        'PortariaParametrizacaoModalService',
    ];

    public form: angular.IFormController;
    public portariaEntity: PortariaEntity = {
        status: null,
    };
    public entityCopy: PortariaResponsavelForm = {
        nome: '',
        cpf: '',
    };
    public busy = false;
    public historicoAberto = false;
    public historicoLoading = false;
    public historicoLoadError = false;
    public historicoItems: PortariaHistoricoItemView[] = [];

    constructor(
        public $http: angular.IHttpService,
        public nsjRouting: any,
        public NewToaster: { pop: (opts: { type: string; title: string; body?: string }) => void },
        public PortariaParametrizacaoModalService: PortariaParametrizacaoModalService,
    ) {}

    $onInit() {
        this.getPortariaEntity();
    }

    getPortariaEntity() {
        this.busy = true;
        this.$http({
            method: 'GET',
            url: this.nsjRouting.generate('pontobundle_portaria_index', {}, true),
        }).then((response) => {
            if (!response.data || Array.isArray(response.data)) {
                return;
            }

            this.portariaEntity = response.data as PortariaEntity;
            this.sincronizarFormularioPortaria();
        }).finally(() => {
            this.busy = false;
        });
    }

    ativarPortaria() {
        this.PortariaParametrizacaoModalService.open({ nome: '', cpf: '' }).result
            .then((response) => {
                this.busy = true;

                this.$http({
                    method: 'POST',
                    url: this.nsjRouting.generate('pontobundle_portaria_insert', {}, true),
                    data: response
                }).then((postResponse) => {
                    this.portariaEntity = postResponse.data as PortariaEntity;
                    this.sincronizarFormularioPortaria();
                }).catch((error) => {
                    this.NewToaster.pop({
                        type: 'error',
                        title: error?.data?.message || 'Não foi possível concluir a parametrização da portaria.'
                    });
                }).finally(() => {
                    this.busy = false;
                });
            }).catch(() => { /**/ });
    }

    sincronizarFormularioPortaria() {
        this.entityCopy = {
            nome: this.portariaEntity?.nome || '',
            cpf: this.portariaEntity?.cpf || '',
        };

        if (this.form) {
            this.form.$setPristine();
            this.form.$setUntouched();
            this.form.$submitted = false;
        }
    }

    canEditPortariaResponsavel() {
        return !!this.portariaEntity?.portaria
            && this.portariaEntity.status !== null
            && this.portariaEntity.status !== 3;
    }

    hasPortariaResponsavelChanges() {
        if (!this.canEditPortariaResponsavel()) {
            return false;
        }

        return this.normalizarNomePortaria(this.entityCopy.nome) !== this.normalizarNomePortaria(this.portariaEntity.nome)
            || this.normalizarCpfPortaria(this.entityCopy.cpf) !== this.normalizarCpfPortaria(this.portariaEntity.cpf);
    }

    salvarResponsavelPortaria() {
        if (!this.canEditPortariaResponsavel() || !this.portariaEntity.portaria || !this.form) {
            return;
        }

        this.form.$submitted = true;
        if (this.form.$invalid || !this.hasPortariaResponsavelChanges()) {
            return;
        }

        this.busy = true;
        this.$http({
            method: 'PUT',
            url: this.nsjRouting.generate('pontobundle_portaria_update_responsavel', {
                portaria: this.portariaEntity.portaria,
            }, true),
            data: angular.copy(this.entityCopy),
        }).then((response) => {
            this.portariaEntity = response.data as PortariaEntity;
            this.sincronizarFormularioPortaria();

            this.NewToaster.pop({
                type: 'success',
                title: 'Sucesso',
                body: 'Responsável legal atualizado com sucesso!'
            });

            if (this.historicoAberto) {
                this.carregarHistoricoPortaria();
            }
        }).catch((error) => {
            this.NewToaster.pop({
                type: 'error',
                title: error?.data?.message || 'Não foi possível atualizar o responsável legal da portaria.'
            });
        }).finally(() => {
            this.busy = false;
        });
    }

    abrirHistoricoPortaria() {
        if (!this.portariaEntity?.portaria) {
            return;
        }

        this.historicoAberto = true;
        this.carregarHistoricoPortaria();
    }

    fecharHistoricoPortaria() {
        this.historicoAberto = false;
    }

    carregarHistoricoPortaria() {
        if (!this.portariaEntity?.portaria) {
            return;
        }

        this.historicoLoading = true;
        this.historicoLoadError = false;
        this.historicoItems = [];

        this.$http({
            method: 'GET',
            url: this.nsjRouting.generate('pontobundle_portaria_historicos', {
                portaria: this.portariaEntity.portaria,
            }, true),
        }).then((response) => {
            const historicos = Array.isArray(response.data) ? response.data as PortariaHistoricoEntity[] : [];
            this.historicoItems = this.transformarHistoricoPortaria(historicos);
        }).catch(() => {
            this.historicoLoadError = true;
            this.historicoItems = [];
        }).finally(() => {
            this.historicoLoading = false;
        });
    }

    transformarHistoricoPortaria(historicos: PortariaHistoricoEntity[]) {
        return historicos.map((historico, index) => {
            const isUltimoItem = index === historicos.length - 1;

            return {
                id: historico.portariahistorico,
                titulo: isUltimoItem ? 'Responsável legal definido' : 'Responsável legal alterado',
                dataHora: this.formatarDataHoraHistoricoPortaria(historico.created_at || historico.lastupdate),
                responsavel: historico.created_by?.nome || '',
                descricao: `Nome: ${historico.nome || '-'} | CPF: ${this.formatarCpfPortaria(historico.cpf) || '-'}`,
                indicatorClass: isUltimoItem ? 'indicator--info' : 'indicator--success',
            };
        });
    }

    private normalizarNomePortaria(nome?: string) {
        return (nome || '').trim();
    }

    private normalizarCpfPortaria(cpf?: string) {
        return (cpf || '').replace(/\D/g, '');
    }

    private formatarCpfPortaria(cpf?: string) {
        const cpfNormalizado = this.normalizarCpfPortaria(cpf);

        if (!cpfNormalizado) {
            return '';
        }

        return cpfNormalizado
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    }

    private formatarDataHoraHistoricoPortaria(data?: string) {
        if (!data) {
            return '';
        }

        return moment(data).format('DD/MM/YYYY [às] HH:mm');
    }
}
